import type { Database } from '@shady-pines/db';
import { ZodUrl } from '@shady-pines/types';
import type { SupportedStorage } from '@supabase/supabase-js';
import { createClient } from '@supabase/supabase-js';
import { isNil } from 'lodash';

export type SupabaseAnonKey = string & {
  __anonKey: 'SupabaseAnonKey';
};

// Rate limiter with timeout, queue limit, and retry with exponential backoff
const createRateLimitedFetch = (
  maxRequests: number,
  windowMs: number,
  maxQueueSize: number = 100,
  maxRetries: number = 3,
  errorCallback?: (err: any) => void,
) => {
  const requestTimestamps: number[] = [];
  let queuedRequests = 0;

  const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  const tryRequest = async (
    input: Parameters<typeof fetch>[0],
    init: Parameters<typeof fetch>[1] | undefined,
    attempt: number,
  ): Promise<Response> => {
    const now = Date.now();

    console.debug('[RateLimiter] tryRequest called', {
      attempt,
      currentTimestamps: requestTimestamps.length,
      maxRequests,
      windowMs,
      queuedRequests,
    });

    // Remove timestamps outside the current window
    const timestampsBeforeCleanup = requestTimestamps.length;
    while (requestTimestamps.length > 0 && now - requestTimestamps[0] > windowMs) {
      requestTimestamps.shift();
    }

    if (timestampsBeforeCleanup > requestTimestamps.length) {
      console.debug('[RateLimiter] Cleaned up old timestamps', {
        removed: timestampsBeforeCleanup - requestTimestamps.length,
        remaining: requestTimestamps.length,
        oldestTimestamp: requestTimestamps[0] ? now - requestTimestamps[0] : null,
      });
    }

    // If at max capacity, reject immediately
    if (requestTimestamps.length >= maxRequests) {
      console.debug('[RateLimiter] Rate limit exceeded!', {
        currentRequests: requestTimestamps.length,
        maxRequests,
        windowMs,
        oldestRequestAge: requestTimestamps[0] ? now - requestTimestamps[0] : null,
        queuedRequests,
      });
      throw new Error(`Rate limit exceeded: ${maxRequests} requests per ${windowMs}ms`);
    }

    // Record this request
    requestTimestamps.push(Date.now());
    console.debug('[RateLimiter] Request recorded', {
      totalInWindow: requestTimestamps.length,
      maxRequests,
    });

    try {
      const response = await fetch(input, init);

      console.debug('[RateLimiter] Response received', {
        status: response.status,
        attempt,
      });

      // Retry on 429 (Too Many Requests) or 5xx errors
      if ((response.status === 429 || response.status >= 500) && attempt < maxRetries) {
        const backoffMs = Math.min(1000 * Math.pow(2, attempt), 10000); // Cap at 10s
        console.warn('[RateLimiter] Retrying due to error response', {
          status: response.status,
          attempt,
          nextAttempt: attempt + 1,
          backoffMs,
        });
        await wait(backoffMs);
        return await tryRequest(input, init, attempt + 1);
      }

      return response;
    } catch (error) {
      console.debug('[RateLimiter] Request failed with error', {
        error,
        attempt,
      });

      // Retry on network errors with exponential backoff
      if (attempt < maxRetries) {
        const backoffMs = Math.min(1000 * Math.pow(2, attempt), 10000);
        const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
        console.warn('[RateLimiter] Retrying after network error', {
          url,
          attempt,
          nextAttempt: attempt + 1,
          backoffMs,
        });
        await wait(backoffMs);
        return tryRequest(input, init, attempt + 1);
      }
      throw error;
    }
  };

  return async (input: Parameters<typeof fetch>[0], init?: Parameters<typeof fetch>[1]) => {
    console.debug('[RateLimiter] New request incoming', {
      queuedRequests,
      maxQueueSize,
      currentTimestamps: requestTimestamps.length,
      maxRequests,
    });

    // Check queue size before queuing
    if (queuedRequests >= maxQueueSize) {
      console.error('[RateLimiter] Queue full!', {
        queuedRequests,
        maxQueueSize,
      });
      throw new Error(`Rate limiter queue full (max: ${maxQueueSize})`);
    }

    queuedRequests++;
    console.debug('[RateLimiter] Request queued', {
      queuedRequests,
      maxQueueSize,
    });

    try {
      const result = await tryRequest(input, init, 0);
      console.debug('[RateLimiter] Request completed successfully', {
        queuedRequests: queuedRequests - 1,
      });
      return result;
    } catch (err) {
      errorCallback?.(err);
      throw err;
    } finally {
      queuedRequests--;
    }
  };
};

const noOpLock = async (name: string, acquireTimeout: number, fn: () => Promise<any>) => {
  const result = await fn();
  return result;
};

const createLoggingFetch = (baseFetch: typeof fetch = fetch) => {
  return async (input: Parameters<typeof fetch>[0], init?: Parameters<typeof fetch>[1]) => {
    console.debug('[Supabase Request]', {
      url: input,
      method: init?.method || 'GET',
      headers: init?.headers,
      body: init?.body,
    });

    const response = await baseFetch(input, init);

    const clonedResponse = response.clone();
    const responseBody = await clonedResponse.text();

    console.debug('[Supabase Response]', {
      url: input,
      status: response.status,
      headers: Object.fromEntries(response.headers.entries()),
      body: responseBody,
    });

    return response;
  };
};

export const createSupabaseClient = (
  supabaseUrl: string,
  anonKey: SupabaseAnonKey,
  storage?: SupportedStorage,
  rateLimitConfig?: {
    maxRequests: number;
    windowMs: number;
    maxQueueSize?: number;
    maxWaitMs?: number;
    maxRetries?: number;
  },
  errorCallback?: (err: any) => void,
) => {
  return createClient<Database>(ZodUrl.parse(supabaseUrl), anonKey, {
    auth: {
      storage,
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: false,

      lock: noOpLock,
    },
    global: {
      fetch: isNil(rateLimitConfig)
        ? createLoggingFetch()
        : createRateLimitedFetch(
            rateLimitConfig.maxRequests,
            rateLimitConfig.windowMs,
            rateLimitConfig.maxQueueSize,
            rateLimitConfig.maxRetries,
            errorCallback,
          ),
    },
  });
};
